import { computed, type Ref } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { ANNOUNCEMENT_AUDIENCE_OPTIONS } from '@/graphql/schoolAnnouncements';
import type { AnnouncementAudienceOptionsQuery } from '@/types/operations';

/**
 * The school's class levels and arms to aim a message at (the notice board
 * and data requests share the query). Levels are derived from the arms.
 */
export function useAudienceClassOptions(options: { enabled?: Ref<boolean>; tertiary?: Ref<boolean> } = {}) {
  const { result, loading, error } = useQuery<AnnouncementAudienceOptionsQuery>(
    ANNOUNCEMENT_AUDIENCE_OPTIONS,
    {},
    () => ({ enabled: options.enabled?.value ?? true }),
  );

  const sections = computed(() => (result.value?.getSchool?.classroomSections ?? []).filter(s => s !== null));
  const arms = computed(() => sections.value.map(s => ({ value: s.id, label: s.name ?? 'Unnamed arm' })));
  const levels = computed(() => {
    const seen = new Map<string, string>();
    sections.value.forEach((s) => {
      // A tertiary level has one arm whose name reads "400 Level"; the bare
      // level is just "400".
      if (s.classroom && !seen.has(s.classroom.id)) seen.set(s.classroom.id, options.tertiary?.value && s.name ? s.name : s.classroom.name);
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  });

  return { levels, arms, loading, error };
}
